import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import toast from "react-hot-toast";
import debounce from "lodash/debounce";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";
import {
  TrendingUp,
  DollarSign,
  CreditCard,
  Award,
  Copy,
  Eye,
  RotateCcw,
  CheckCircle,
  Clock,
  XCircle,
  ArrowUpRight,
  Search as SearchIcon,
  Download,
  Layers,
  Filter,
} from "lucide-react";
import Spinner from "../components/Common/Spinner";
import Pagination from "../components/Pagination";
import {
  getEarnings,
  getEarningDetails,
  getSubscriptionPlans,
  getEarningGraph,
} from "../../services/Admin/adminServices";

const PARTNER_COLORS = ["#87C55F", "#36A2EB", "#FFCE56", "#9966FF", "#FF6384", "#4BC0C0", "#FF9F40"];
const getPartnerColor = (idx) => PARTNER_COLORS[idx % PARTNER_COLORS.length];

const Earnings = () => {
  // Data State
  const [earnings, setEarnings] = useState([]);
  const [stats, setStats] = useState({
    totalEarnings: 0,
    todayEarnings: 0,
    monthlyEarnings: 0,
    totalRefundedAmount: 0,
    totalTransactions: 0,
    successfulTransactions: 0,
    pendingTransactions: 0,
    failedTransactions: 0,
    refundedTransactions: 0,
  });
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [plansList, setPlansList] = useState([]);

  // Partner Graph State
  const [graphData, setGraphData] = useState({ plans: [], timeline: [] });
  const [graphPeriod, setGraphPeriod] = useState("weekly");
  const [graphPlanId, setGraphPlanId] = useState("all");
  const [graphStartDate, setGraphStartDate] = useState("");
  const [graphEndDate, setGraphEndDate] = useState("");
  const [graphLoading, setGraphLoading] = useState(false);

  // Filters State (search, status, planId, startDate, endDate)
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Modal State
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEarning, setSelectedEarning] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Handle Search Debounce
  useEffect(() => {
    setCurrentPage(0);
    const handler = debounce(() => {
      setDebouncedSearch(search);
    }, 500);

    handler();
    return () => {
      handler.cancel();
    };
  }, [search]);

  // Fetch Subscription Plans list for dropdown filter (reference from Subscriptions.js)
  const fetchPlansList = async () => {
    try {
      const res = await getSubscriptionPlans(0, 100);
      const planItems = res?.data?.data?.subscriptionPlans || [];
      setPlansList(planItems);
    } catch (err) {
      console.error("Could not fetch subscription plans list:", err);
    }
  };

  useEffect(() => {
    fetchPlansList();
  }, []);

  // Fetch Partner Packages Graph Data from GET /api/admin/earning/graph
  const fetchGraphData = useCallback(async () => {
    try {
      setGraphLoading(true);
      const res = await getEarningGraph(graphStartDate, graphEndDate, graphPlanId, graphPeriod);
      const gData = res?.data?.data;

      if (gData) {
        // 1. Parse plans list (or infer from datasets)
        let plansList = gData.plans || [];
        if ((!plansList || plansList.length === 0) && gData.datasets && gData.datasets.length > 0) {
          plansList = gData.datasets.map((d, i) => ({
            planId: d.planId || `plan-${i}`,
            name: d.label || `Plan ${i + 1}`,
          }));
        }

        // 2. Format timeline objects so Recharts can read plan names & total revenue at top-level
        let formattedTimeline = [];

        if (gData.timeline && gData.timeline.length > 0) {
          formattedTimeline = gData.timeline.map((item) => {
            const total = item.totalRevenue ?? item.total ?? 0;
            const entry = {
              label: item.label || "Period",
              total: total,
              startDate: item.startDate,
              endDate: item.endDate,
            };

            // Populate revenue for each plan on entry[plan.name]
            plansList.forEach((plan) => {
              const pName = plan.name;
              let val = 0;

              if (item.breakdown && item.breakdown[pName] !== undefined) {
                val = item.breakdown[pName];
              } else if (item.breakdownList && Array.isArray(item.breakdownList)) {
                const bItem = item.breakdownList.find(
                  (b) => b.name === pName || b.planId === plan.planId
                );
                if (bItem) val = bItem.revenue ?? bItem.total ?? 0;
              } else if (item[pName] !== undefined) {
                val = item[pName];
              }

              entry[pName] = val;
            });

            return entry;
          });
        } else if (gData.labels && gData.labels.length > 0) {
          // Fallback: Construct timeline from labels, values, datasets if timeline array is missing
          formattedTimeline = gData.labels.map((label, idx) => {
            const total = gData.values?.[idx] ?? 0;
            const entry = {
              label: label,
              total: total,
            };

            (gData.datasets || []).forEach((ds) => {
              const pName = ds.label;
              entry[pName] = ds.data?.[idx] ?? 0;
            });

            return entry;
          });
        }

        setGraphData({
          plans: plansList,
          timeline: formattedTimeline,
          grandTotalRevenue: gData.grandTotalRevenue ?? 0,
        });
      } else {
        setGraphData({ plans: [], timeline: [], grandTotalRevenue: 0 });
      }
    } catch (err) {
      console.error("Failed to fetch earning graph:", err);
      setGraphData({ plans: [], timeline: [], grandTotalRevenue: 0 });
    } finally {
      setGraphLoading(false);
    }
  }, [graphStartDate, graphEndDate, graphPlanId, graphPeriod]);

  useEffect(() => {
    fetchGraphData();
  }, [fetchGraphData]);

  // Fetch Earnings Data from GET /api/admin/earning with filters
  const fetchEarningsData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getEarnings(
        currentPage,
        itemsPerPage,
        debouncedSearch,
        statusFilter,
        planFilter,
        startDate,
        endDate
      );

      const resData = res?.data?.data;
      if (resData) {
        setEarnings(resData.earnings || []);
        if (resData.stats) {
          setStats(resData.stats);
        }
        setTotalItems(resData.total || 0);
      } else {
        setEarnings([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching earnings data:", error);
      const msg = error?.response?.data?.message || "Failed to fetch earnings records";
      toast.error(msg);
      setEarnings([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearch, statusFilter, planFilter, startDate, endDate]);

  useEffect(() => {
    fetchEarningsData();
  }, [fetchEarningsData]);

  // Clear all filters (Resets search, statusFilter, planFilter, startDate, endDate)
  const handleClearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setStatusFilter("all");
    setPlanFilter("all");
    setStartDate("");
    setEndDate("");
    setCurrentPage(0);
  };

  // CSV Export Handler
  const handleExportCSV = () => {
    if (!graphData?.timeline || graphData.timeline.length === 0) {
      toast.error("No graph data available to export.");
      return;
    }

    const plans = graphData.plans || [];
    const headers = ["Period", ...plans.map((p) => `"${p.name} (₹)"`), "Total Revenue (₹)"];

    const rows = graphData.timeline.map((item) => {
      const planCols = plans.map((p) => item[p.name] ?? 0);
      return [`"${item.label}"`, ...planCols, item.total ?? 0].join(",");
    });

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `revenue_partner_packages_${graphPeriod}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Revenue report exported as CSV!");
  };

  // View Earning Details Modal
  const handleViewDetails = async (earning) => {
    setSelectedEarning(earning);
    setShowDetailModal(true);
    if (earning?._id) {
      try {
        setDetailLoading(true);
        const res = await getEarningDetails(earning._id);
        if (res?.data?.data) {
          setSelectedEarning(res.data.data);
        }
      } catch (err) {
        console.error("Error loading transaction details:", err);
      } finally {
        setDetailLoading(false);
      }
    }
  };

  // Copy to clipboard helper
  const handleCopyText = (text, label) => {
    if (text) {
      navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    }
  };

  // Status Badge Helper
  const renderStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge bg="success" className="px-2 py-1"><CheckCircle size={12} className="me-1" /> Completed</Badge>;
      case "pending":
        return <Badge bg="warning" className="text-white px-2 py-1"><Clock size={12} className="me-1" /> Pending</Badge>;
      case "failed":
        return <Badge bg="danger" className="px-2 py-1"><XCircle size={12} className="me-1" /> Failed</Badge>;

      default:
        return <Badge bg="secondary" className="px-2 py-1">{status || "Unknown"}</Badge>;
    }
  };

  // Method Badge Helper
  const renderMethodBadge = (method) => {
    const m = (method || "online").toLowerCase();
    let bg = "bg-info";
    if (m === "upi") bg = "bg-primary";
    if (m === "card") bg = "bg-dark";
    if (m === "netbanking") bg = "bg-success";
    if (m === "wallet") bg = "bg-warning text-dark";

    return <span className={`badge ${bg} px-2 py-1 text-uppercase`}>{m}</span>;
  };

  // Custom Tooltip for Partner Packages Stacked Bar Chart
  const CustomPartnerTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      return (
        <div
          className="bg-white p-3 shadow-lg rounded border"
          style={{ minWidth: "220px", backgroundColor: "#ffffff" }}
        >
          <p className="fw-bold mb-2 text-dark border-bottom pb-1">{label}</p>
          {payload.map((entry, idx) => (
            <div key={idx} className="d-flex justify-content-between align-items-center mb-1 small">
              <span className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: "10px",
                    height: "10px",
                    backgroundColor: entry.color,
                    borderRadius: "50%",
                    display: "inline-block",
                  }}
                />
                {entry.name}:
              </span>
              <strong className="ms-3 text-dark">₹{entry.value?.toLocaleString("en-IN")}</strong>
            </div>
          ))}
          <div className="d-flex justify-content-between align-items-center pt-2 mt-2 border-top fw-bold text-dark">
            <span>Total Period Revenue:</span>
            <span style={{ color: "#87C55F" }}>₹{total.toLocaleString("en-IN")}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  return (
    <div className="container mt-4">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 style={{ color: "#87C55F" }} className="mb-0">Earnings & Revenue Management</h1>
          <small className="text-muted">
            Comprehensive financial dashboard, partner package analytics, and transaction records.
          </small>
        </div>
      </div>

      {/* Revenue Summary Metrics Grid */}
      <Row className="g-3 mb-4">
        {/* Total Earnings */}
        <Col lg={3} md={6}>
          <div
            className="card shadow-sm border-0 bg-white p-3 h-100"
            style={{ borderRadius: "12px", backgroundColor: "#ffffff" }}
          >
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <span className="text-muted fw-semibold text-uppercase small">Total Earnings</span>
                <h3 className="fw-bold my-1 text-dark">
                  ₹{stats?.totalEarnings?.toLocaleString("en-IN") || 0}
                </h3>
                <span className="text-success small fw-bold">
                  <TrendingUp size={14} className="me-1" /> Lifetime Revenue
                </span>
              </div>
              <div
                className="p-3 rounded-circle"
                style={{ backgroundColor: "rgba(135, 197, 95, 0.15)", color: "#87C55F" }}
              >
                <DollarSign size={22} />
              </div>
            </div>
          </div>
        </Col>

        {/* Today's Earnings */}
        <Col lg={3} md={6}>
          <div
            className="card shadow-sm border-0 bg-white p-3 h-100"
            style={{ borderRadius: "12px", backgroundColor: "#ffffff" }}
          >
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <span className="text-muted fw-semibold text-uppercase small">Today's Earnings</span>
                <h3 className="fw-bold my-1 text-dark">
                  ₹{stats?.todayEarnings?.toLocaleString("en-IN") || 0}
                </h3>
                <span className="text-primary small fw-bold">
                  <ArrowUpRight size={14} className="me-1" /> Today
                </span>
              </div>
              <div
                className="p-3 rounded-circle"
                style={{ backgroundColor: "rgba(54, 162, 235, 0.15)", color: "#36A2EB" }}
              >
                <CreditCard size={22} />
              </div>
            </div>
          </div>
        </Col>

        {/* Monthly Earnings */}
        <Col lg={3} md={6}>
          <div
            className="card shadow-sm border-0 bg-white p-3 h-100"
            style={{ borderRadius: "12px", backgroundColor: "#ffffff" }}
          >
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <span className="text-muted fw-semibold text-uppercase small">Monthly Earnings</span>
                <h3 className="fw-bold my-1 text-dark">
                  ₹{stats?.monthlyEarnings?.toLocaleString("en-IN") || 0}
                </h3>
                <span className="text-muted small fw-bold">Current Month</span>
              </div>
              <div
                className="p-3 rounded-circle"
                style={{ backgroundColor: "rgba(255, 206, 86, 0.15)", color: "#FFCE56" }}
              >
                <Award size={22} />
              </div>
            </div>
          </div>
        </Col>

        {/* Total Refunded */}
        <Col lg={3} md={6}>
          <div
            className="card shadow-sm border-0 bg-white p-3 h-100"
            style={{ borderRadius: "12px", backgroundColor: "#ffffff" }}
          >
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <span className="text-muted fw-semibold text-uppercase small">Total Refunded</span>
                <h3 className="fw-bold my-1 text-dark">
                  ₹{stats?.totalRefundedAmount?.toLocaleString("en-IN") ?? 0}
                </h3>
                <span className="text-secondary small fw-bold">
                  {stats?.refundedTransactions ?? 0} Refunded Txns
                </span>
              </div>
              <div
                className="p-3 rounded-circle"
                style={{ backgroundColor: "rgba(153, 102, 255, 0.15)", color: "#9966FF" }}
              >
                <RotateCcw size={22} />
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Transaction Overview & Breakdown Strip (Showing all 9 stats fields) */}
      <div
        className="p-3 mb-4 bg-white rounded shadow-sm d-flex flex-wrap align-items-center justify-content-between gap-3"
        style={{ backgroundColor: "#ffffff", border: "1px solid #e9ecef" }}
      >
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="fw-bold text-dark me-2">Transaction Volume Breakdown:</span>

          <span className="badge bg-light text-dark border px-3 py-2">
            Total Txns: <strong>{stats?.totalTransactions ?? 0}</strong>
          </span>

          <span className="badge bg-success px-3 py-2 text-white">
            Successful: <strong>{stats?.successfulTransactions ?? 0}</strong>
          </span>

          <span className="badge bg-warning text-white px-3 py-2">
            Pending: <strong>{stats?.pendingTransactions ?? 0}</strong>
          </span>

          <span className="badge bg-danger px-3 py-2 text-white">
            Failed: <strong>{stats?.failedTransactions ?? 0}</strong>
          </span>


        </div>
      </div>


      <div
        className="card shadow-sm border-0 bg-white mb-4"
        style={{ borderRadius: "12px", backgroundColor: "#ffffff" }}
      >
        <div
          className="card-header bg-white py-3 border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2"
          style={{ backgroundColor: "#ffffff" }}
        >
          <div>
            <h5 className="fw-bold mb-0 text-dark flex items-center gap-2">
              <Layers size={20} style={{ color: "#87C55F" }} className="me-1 inline" />
              Revenue Generated by Subscription
            </h5>
            <small className="text-muted d-flex align-items-center gap-2 mt-1">
              <span>Revenue contribution breakdown per Subscription</span>
              {graphData.grandTotalRevenue > 0 && (
                <span className="badge bg-success text-white px-2 py-1">
                  Grand Total: ₹{graphData.grandTotalRevenue.toLocaleString("en-IN")}
                </span>
              )}
            </small>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Form.Select
              size="sm"
              value={graphPeriod}
              onChange={(e) => setGraphPeriod(e.target.value)}
              style={{
                height: "40px",
                borderRadius: "0.5rem",
                borderColor: "#87C55F",
                width: "120px",
                fontWeight: "600",
                boxShadow: "0 2px 6px rgba(135, 197, 95, 0.12)",
              }}
            >
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
            </Form.Select>

            {/* Package Select Filter Dropdown */}
            <Form.Select
              size="sm"
              value={graphPlanId}
              onChange={(e) => setGraphPlanId(e.target.value)}
              style={{
                height: "40px",
                borderRadius: "0.5rem",
                borderColor: "#87C55F",
                width: "200px",
                fontWeight: "600",
                boxShadow: "0 2px 6px rgba(135, 197, 95, 0.12)",
              }}
            >
              <option value="all">All Subscriptions</option>
              {plansList.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name}
                </option>
              ))}
            </Form.Select>

            {/* Graph Date Range Filter (Start Date & End Date) */}
            <div className="d-flex align-items-center gap-1">
              <input
                type="date"
                className="form-control form-control-sm"
                value={graphStartDate}
                onChange={(e) => setGraphStartDate(e.target.value)}
                title="Graph Start Date"
                style={{
                  height: "40px",
                  borderRadius: "0.5rem",
                  borderColor: "#87C55F",
                  boxShadow: "0 2px 6px rgba(135, 197, 95, 0.12)",
                  fontSize: "13px",
                  fontWeight: "500",
                }}
              />
              <span className="text-muted small fw-bold px-1">to</span>
              <input
                type="date"
                className="form-control form-control-sm"
                value={graphEndDate}
                onChange={(e) => setGraphEndDate(e.target.value)}
                title="Graph End Date"
                style={{
                  height: "40px",
                  borderRadius: "0.5rem",
                  borderColor: "#87C55F",
                  boxShadow: "0 2px 6px rgba(135, 197, 95, 0.12)",
                  fontSize: "13px",
                  fontWeight: "500",
                }}
              />
            </div>

            {(graphStartDate || graphEndDate) && (
              <Button
                size="sm"
                variant="outline-danger"
                style={{
                  height: "40px",
                  borderRadius: "0.5rem",
                  fontWeight: "600",
                  padding: "0 12px",
                }}
                onClick={() => {
                  setGraphStartDate("");
                  setGraphEndDate("");
                }}
              >
                Clear Dates
              </Button>
            )}

          </div>
        </div>

        <div className="card-body bg-white p-3" style={{ backgroundColor: "#ffffff", height: "420px" }}>
          {graphLoading ? (
            <Spinner />
          ) : graphData.timeline && graphData.timeline.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={graphData.timeline}
                margin={{ top: 30, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e9ecef" />
                <XAxis dataKey="label" stroke="#6c757d" tick={{ fontSize: 12, fontWeight: 500 }} />
                <YAxis
                  stroke="#6c757d"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(val) => `₹${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                />
                <Tooltip content={<CustomPartnerTooltip />} />

                {/* Stacked Bars dynamically rendered for each plan */}
                {(graphData.plans || []).map((plan, index) => {
                  const isTopBar = index === (graphData.plans || []).length - 1;
                  return (
                    <Bar
                      key={plan._id || plan.name}
                      dataKey={plan.name}
                      stackId="a"
                      fill={getPartnerColor(index)}
                      radius={isTopBar ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    >
                      {/* Label Total Revenue Badge above Top Bar */}
                      {isTopBar && (
                        <LabelList
                          dataKey="total"
                          position="top"
                          formatter={(val) => `₹${val?.toLocaleString("en-IN")}`}
                          style={{ fill: "#333333", fontSize: "12px", fontWeight: "700" }}
                        />
                      )}
                    </Bar>
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              No revenue graph data available for the selected period/filters.
            </div>
          )}
        </div>


        {graphData.plans && graphData.plans.length > 0 && (
          <div className="card-footer bg-white border-top py-3 px-3 d-flex flex-wrap justify-content-center gap-4" style={{ backgroundColor: "#ffffff" }}>
            {graphData.plans.map((plan, index) => (
              <div key={plan._id || index} className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: getPartnerColor(index),
                    borderRadius: "3px",
                    display: "inline-block",
                  }}
                />
                <span className="fw-semibold text-dark small">{plan.name} (₹)</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        className="p-3 mb-4 bg-white rounded shadow-sm"
        style={{ backgroundColor: "#ffffff", border: "1px solid #e9ecef" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <Filter size={18} style={{ color: "#87C55F" }} className="me-2" />
            <h6 className="fw-bold mb-0 text-dark">Filter Transactions</h6>
          </div>
          {(search || statusFilter !== "all" || planFilter !== "all" || startDate || endDate) && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleClearFilters}
              style={{
                borderRadius: "0.5rem",
                fontWeight: "600",
                height: "36px",
                padding: "0 16px",
              }}
            >
              Reset Filters
            </Button>
          )}
        </div>

        <Row className="g-3 align-items-end">
          {/* Search Input */}
          <Col lg={3} md={6}>
            <label className="form-label fw-semibold small mb-1">Search</label>
            <div className="position-relative">
              <input
                type="text"
                className="form-control ps-5"
                placeholder="Search user, email, payment ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  height: "44px",
                  borderRadius: "0.5rem",
                  borderColor: "#87C55F",
                  boxShadow: "0 2px 6px rgba(135, 197, 95, 0.12)",
                }}
              />
              <SearchIcon
                size={18}
                className="position-absolute text-muted"
                style={{ left: "14px", top: "13px" }}
              />
            </div>
          </Col>

          {/* Status Filter Dropdown */}
          <Col lg={2} md={6}>
            <label className="form-label fw-semibold small mb-1">Status</label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(0);
              }}
              style={{
                height: "44px",
                borderRadius: "0.5rem",
                borderColor: "#87C55F",
                boxShadow: "0 2px 6px rgba(135, 197, 95, 0.12)",
              }}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </Form.Select>
          </Col>

          {/* Plan Filter Dropdown (planId) */}
          <Col lg={3} md={6}>
            <label className="form-label fw-semibold small mb-1">Subscription Plan</label>
            <Form.Select
              value={planFilter}
              onChange={(e) => {
                setPlanFilter(e.target.value);
                setCurrentPage(0);
              }}
              style={{
                height: "44px",
                borderRadius: "0.5rem",
                borderColor: "#87C55F",
                boxShadow: "0 2px 6px rgba(135, 197, 95, 0.12)",
              }}
            >
              <option value="all">All Subscription Plans</option>
              {plansList.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name}
                </option>
              ))}
            </Form.Select>
          </Col>

          {/* Start Date & End Date Range Picker */}
          <Col lg={4} md={6}>
            <label className="form-label fw-semibold small mb-1">Date Range (Start & End Date)</label>
            <div className="d-flex align-items-center gap-2">
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setCurrentPage(0);
                }}
                style={{
                  height: "44px",
                  borderRadius: "0.5rem",
                  borderColor: "#87C55F",
                  boxShadow: "0 2px 6px rgba(135, 197, 95, 0.12)",
                }}
              />
              <span className="text-muted small fw-bold">to</span>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setCurrentPage(0);
                }}
                style={{
                  height: "44px",
                  borderRadius: "0.5rem",
                  borderColor: "#87C55F",
                  boxShadow: "0 2px 6px rgba(135, 197, 95, 0.12)",
                }}
              />
            </div>
          </Col>
        </Row>
      </div>

      {/* TRANSACTIONS DATA TABLE */}
      {loading ? (
        <Spinner />
      ) : (
        <div className="table-responsive" style={{ overflowX: "auto" }}>
          <table className="table table-striped align-middle shadow-sm">
            <thead className="table-light">
              <tr>
                <th>Sr No.</th>
                <th>User</th>
                <th>Plan Name</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Status</th>
                <th>Razorpay Payment ID</th>
                <th>Date & Time</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {earnings?.length > 0 ? (
                earnings.map((item, index) => (
                  <tr key={item._id || index}>
                    <td>{currentPage * itemsPerPage + index + 1}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-dark fw-bold"
                          style={{
                            width: "36px",
                            height: "36px",
                            backgroundColor: "rgba(135, 197, 95, 0.15)",
                            color: "#87C55F",
                            border: "1px solid #87C55F",
                            flexShrink: 0,
                          }}
                        >
                          {item.userId?.name ? item.userId.name.charAt(0).toUpperCase() : "U"}
                        </div>
                        <div>
                          <div className="fw-bold text-dark">{item.userId?.name || "Anonymous User"}</div>
                          <small className="text-muted d-block">{item.email || item.userId?.email || "No email"}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="fw-semibold text-dark">{item.planId?.name || "Subscription Plan"}</span>
                      {item.planId?.period && (
                        <small className="text-muted d-block capitalize">Cycle: {item.planId.period}</small>
                      )}
                    </td>
                    <td>
                      <span style={{ color: "#87C55F", fontWeight: "700" }}>
                        ₹{item.amount?.toLocaleString("en-IN")}
                      </span>{" "}
                      <small className="text-muted">{item.currency || "INR"}</small>
                    </td>
                    <td>{renderMethodBadge(item.method)}</td>
                    <td>{renderStatusBadge(item.status)}</td>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        <code className="small">{item.razorpayPaymentId || "N/A"}</code>
                        {item.razorpayPaymentId && (
                          <span
                            style={{ cursor: "pointer", color: "#87C55F" }}
                            onClick={() => handleCopyText(item.razorpayPaymentId, "Payment ID")}
                          >
                            <Copy size={14} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                        : "N/A"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <Button
                        size="sm"
                        style={{ backgroundColor: "#87C55F", border: "none" }}
                        onClick={() => handleViewDetails(item)}
                      >
                        <Eye size={14} className="me-1" /> View Details
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-5">
                    <div className="text-muted">
                      <SearchIcon size={36} className="mb-2 text-secondary" />
                      <h5>No earning records found</h5>
                      <p className="small mb-0">No transaction data matches the selected filters.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINATION */}
      {earnings?.length > 0 && (
        <div className="card-footer clearfix bg-white border-0 py-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center gap-3">
              <div className="dataTables_info" style={{ color: "#87C55F" }}>
                Showing {currentPage * itemsPerPage + 1} to{" "}
                {totalItems > (currentPage + 1) * itemsPerPage
                  ? (currentPage + 1) * itemsPerPage
                  : totalItems}{" "}
                of {totalItems} entries
              </div>

              <div className="d-flex align-items-center gap-1">
                <small className="text-muted">Rows:</small>
                <Form.Select
                  size="sm"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(0);
                  }}
                  style={{ width: "70px" }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </Form.Select>
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <Pagination
                pageCount={totalPages}
                pageValue={currentPage}
                setPage={setCurrentPage}
              />
            </div>
          </div>
        </div>
      )}

      {/* TRANSACTION DETAIL MODAL */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#87C55F" }}>
            Transaction Details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="py-3 px-4" style={{ backgroundColor: "#ffffff" }}>
          {detailLoading ? (
            <Spinner />
          ) : selectedEarning ? (
            <div>
              {/* Header Badge Row */}
              <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <div>
                  <h5 className="mb-1 text-dark fw-bold">
                    Transaction ID: <span style={{ color: "#36A2EB" }} className="font-mono">{selectedEarning._id}</span>
                  </h5>
                  <small className="text-muted">
                    Created on {selectedEarning.createdAt ? new Date(selectedEarning.createdAt).toLocaleString("en-IN") : "N/A"}
                  </small>
                </div>
                <div>{renderStatusBadge(selectedEarning.status)}</div>
              </div>

              {/* Amount Banner */}
              <div
                className="mb-3 p-3 rounded border d-flex justify-content-between align-items-center"
                style={{ backgroundColor: "#f8f9fa", borderColor: "#e0e0e0" }}
              >
                <div>
                  <small className="text-muted d-block fw-semibold">Amount Paid</small>
                  <strong style={{ color: "#87C55F" }} className="fs-4">
                    ₹{selectedEarning.amount?.toLocaleString("en-IN")} {selectedEarning.currency || "INR"}
                  </strong>
                </div>
                <div>
                  <small className="text-muted d-block fw-semibold mb-1">Payment Method</small>
                  {renderMethodBadge(selectedEarning.method)}
                </div>
              </div>

              {/* Grid 1: User & Plan Breakdown */}
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <div className="border rounded overflow-hidden h-100" style={{ backgroundColor: "#ffffff", borderColor: "#e0e0e0" }}>
                    <div className="px-3 py-2 border-bottom fw-bold text-dark" style={{ backgroundColor: "#f8f9fa", fontSize: "14px" }}>
                      User Details
                    </div>
                    <div className="p-3 bg-white" style={{ backgroundColor: "#ffffff" }}>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-dark fw-bold"
                          style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: "rgba(135, 197, 95, 0.15)",
                            color: "#87C55F",
                            border: "1px solid #87C55F",
                            flexShrink: 0,
                          }}
                        >
                          {selectedEarning.userId?.name ? selectedEarning.userId.name.charAt(0).toUpperCase() : "U"}
                        </div>
                        <div>
                          <strong className="d-block text-dark">{selectedEarning.userId?.name || "N/A"}</strong>
                          <small className="text-muted">ID: {selectedEarning.userId?._id || "N/A"}</small>
                        </div>
                      </div>
                      <div className="small mt-3 pt-2 border-top">
                        <div className="mb-1"><strong className="text-dark">Email:</strong> {selectedEarning.email || selectedEarning.userId?.email || "N/A"}</div>
                        <div><strong className="text-dark">Phone:</strong> {selectedEarning.contact || selectedEarning.userId?.phoneNumber || "N/A"}</div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="border rounded overflow-hidden h-100" style={{ backgroundColor: "#ffffff", borderColor: "#e0e0e0" }}>
                    <div className="px-3 py-2 border-bottom fw-bold text-dark" style={{ backgroundColor: "#f8f9fa", fontSize: "14px" }}>
                      Subscription Plan
                    </div>
                    <div className="p-3 bg-white" style={{ backgroundColor: "#ffffff" }}>
                      <h6 className="fw-bold text-dark mb-1">{selectedEarning.planId?.name || "N/A"}</h6>
                      <div className="small text-muted mb-2">
                        Billing Period: <span className="text-dark fw-semibold capitalize">{selectedEarning.planId?.period || "N/A"}</span>
                      </div>
                      <div className="small mt-3 pt-2 border-top">
                        <div className="mb-1"><strong className="text-dark">Plan Price:</strong> ₹{selectedEarning.planId?.amount || selectedEarning.amount}</div>
                        <div><strong className="text-dark">Currency:</strong> {selectedEarning.planId?.currency || "INR"}</div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Grid 2: Razorpay Identifiers & Gateway Breakdown */}
              <Row className="g-3">
                <Col md={6}>
                  <div className="border rounded overflow-hidden h-100" style={{ backgroundColor: "#ffffff", borderColor: "#e0e0e0" }}>
                    <div className="px-3 py-2 border-bottom fw-bold text-dark" style={{ backgroundColor: "#f8f9fa", fontSize: "14px" }}>
                      Razorpay Identifiers
                    </div>
                    <div className="p-3 bg-white small" style={{ backgroundColor: "#ffffff" }}>
                      <div className="mb-2">
                        <small className="text-muted d-block fw-semibold mb-1">Razorpay Payment ID</small>
                        <div className="d-flex align-items-center gap-2">
                          <code style={{ backgroundColor: "#f8f9fa", color: "#333", border: "1px solid #e0e0e0", borderRadius: "4px", padding: "2px 8px", fontSize: "12px" }}>
                            {selectedEarning.razorpayPaymentId || "N/A"}
                          </code>
                          {selectedEarning.razorpayPaymentId && (
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              style={{ padding: "2px 8px", fontSize: "11px" }}
                              onClick={() => handleCopyText(selectedEarning.razorpayPaymentId, "Payment ID")}
                            >
                              Copy
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="mb-2">
                        <small className="text-muted d-block fw-semibold mb-1">Razorpay Subscription ID</small>
                        <div className="d-flex align-items-center gap-2">
                          <code style={{ backgroundColor: "#f8f9fa", color: "#333", border: "1px solid #e0e0e0", borderRadius: "4px", padding: "2px 8px", fontSize: "12px" }}>
                            {selectedEarning.razorpaySubscriptionId || "N/A"}
                          </code>
                          {selectedEarning.razorpaySubscriptionId && (
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              style={{ padding: "2px 8px", fontSize: "11px" }}
                              onClick={() => handleCopyText(selectedEarning.razorpaySubscriptionId, "Subscription ID")}
                            >
                              Copy
                            </Button>
                          )}
                        </div>
                      </div>

                      <div>
                        <small className="text-muted d-block fw-semibold mb-1">Razorpay Order ID</small>
                        <code style={{ backgroundColor: "#f8f9fa", color: "#333", border: "1px solid #e0e0e0", borderRadius: "4px", padding: "2px 8px", fontSize: "12px" }}>
                          {selectedEarning.razorpayOrderId || "N/A"}
                        </code>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="border rounded overflow-hidden h-100" style={{ backgroundColor: "#ffffff", borderColor: "#e0e0e0" }}>
                    <div className="px-3 py-2 border-bottom fw-bold text-dark" style={{ backgroundColor: "#f8f9fa", fontSize: "14px" }}>
                      Gateway Fees & Tax
                    </div>
                    <div className="p-3 bg-white small" style={{ backgroundColor: "#ffffff" }}>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted">Gateway Fee:</span>
                        <strong className="text-dark">₹{selectedEarning.fee ?? 0}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted">GST / Tax:</span>
                        <strong className="text-dark">₹{selectedEarning.tax ?? 0}</strong>
                      </div>
                      {/* <div className="d-flex justify-content-between pt-2 border-top fw-bold text-dark mt-2">
                        <span>Net Settlement:</span>
                        <span style={{ color: "#87C55F" }}>
                          ₹{(selectedEarning.amount - (selectedEarning.fee || 0) - (selectedEarning.tax || 0)).toLocaleString("en-IN")}
                        </span>
                      </div> */}
                      {selectedEarning.vpa && (
                        <div className="mt-2 pt-2 border-top">
                          <span className="text-muted">VPA (UPI ID):</span> <strong className="text-dark">{selectedEarning.vpa}</strong>
                        </div>
                      )}
                      {selectedEarning.bank && (
                        <div className="mt-1">
                          <span className="text-muted">Bank / Wallet:</span> <strong className="text-dark">{selectedEarning.bank}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Footer className="py-2 px-4" style={{ backgroundColor: "#ffffff" }}>
          <Button
            style={{ backgroundColor: "#87C55F", border: "none", padding: "6px 20px" }}
            onClick={() => setShowDetailModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Earnings;
